/* NZS Hero 2a: rotating split-screen homepage hero. */
(function () {
  'use strict';

  var instances = [];

  /* NZS-H2A-DSS (WS1, 2026-08-12): the fade slideshow's follow-cursor and its click halves are
     fine-pointer only. On touch the tap must reach the CTA underneath, and a cursor that cannot
     be moved would just sit stranded wherever the last tap left it. */
  var FINE = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  function qsa(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setReachable(el, active) {
    if (!el) return;
    var focusable = qsa(el, 'a, button, [tabindex]');
    if (el.matches && el.matches('a, button, [tabindex]')) focusable.unshift(el);
    if (active) {
      el.removeAttribute('aria-hidden');
      el.removeAttribute('inert');
      focusable.forEach(function (node) { node.removeAttribute('tabindex'); });
    } else {
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
      focusable.forEach(function (node) { node.setAttribute('tabindex', '-1'); });
    }
  }

  function restart(el, className) {
    if (!el) return;
    el.classList.remove(className);
    // ponytail: reflow restarts the mock keyframes; duplicate A/B keyframe pairs are the heavier alternative.
    void el.offsetWidth;
    el.classList.add(className);
  }

  function Hero2a(el) {
    var self = this;
    this.el = el;
    this.texts = qsa(el, '[data-h2a-text-slide]');
    this.photos = qsa(el, '[data-h2a-photo-slide]');
    this.stackImages = qsa(el, '[data-h2a-stack-image]');
    this.stackCopies = qsa(el, '[data-h2a-stack-copy]');
    this.stackLinks = qsa(el, '[data-h2a-stack-link]');
    this.stackSaves = qsa(el, '[data-h2a-save]');
    this.thumbWraps = qsa(el, '[data-h2a-thumb-wrap]');
    this.thumbs = qsa(el, '[data-h2a-thumb]');
    this.bars = qsa(el, '[data-h2a-thumb-bar]');
    this.rail = el.querySelector('[data-h2a-rail]');
    this.pause = el.querySelector('[data-h2a-pause]');
    this.count = this.texts.length;
    this.current = 0;
    this.delay = Math.max(2000, parseInt(el.getAttribute('data-rotate-ms'), 10) || 5000);
    this.userPaused = el.getAttribute('data-auto-rotate') !== 'true';
    this.hoverPaused = false;
    this.focusPaused = false;
    this.editorPaused = false;
    /* NZS-H2A-MSWIPE (Blake 2026-07-31): true while the mobile swipe hero is the
       one on screen. Rotating a hidden split-screen would be pure cost -- worse,
       preloadNext() would pull full-size desktop photos down on a phone. */
    this.mqPaused = false;
    this.timer = null;
    this.remaining = this.delay;
    this.cycleStart = 0;
    this.outTimer = null;
    this.photoTimer = null;
    this.photoReadyTimer = null;
    this.thumbHandlers = [];

    this.thumbs.forEach(function (thumb, index) {
      var handler = function () {
        self.focusPaused = false;
        self.go(index, true);
      };
      self.thumbHandlers.push(handler);
      thumb.addEventListener('click', handler);
    });

    /* hover pauses in place, and only over the thumbnail CARD itself -- not its
       label, timer bar or the surrounding rail (Blake, 2026-07-30). Resetting is
       stop()'s job; a hover must never reset the bar. */
    /* NZS-H2A-ACTIVEHOVER (Blake 2026-07-31): only the card that is CURRENTLY
       showing pauses the rotation -- hovering any of the other five now does
       nothing. hoverIndex tracks the pointer regardless of which card is
       active, and syncHoverPause() derives the pause state from it, so the
       rule holds in both directions: pointer moves onto the active card, and
       rotation advances onto the card already under the pointer. */
    this.hoverIndex = -1;
    this.onThumbEnter = function (event) {
      var zone = event && event.currentTarget;
      var idx = self.hoverZones.indexOf(zone);
      if (idx < 0) return;
      self.hoverIndex = idx;
      self.syncHoverPause();
    };
    this.onThumbLeave = function () {
      self.hoverIndex = -1;
      self.syncHoverPause();
    };
    this.hoverZones = this.thumbs.map(function (thumb) {
      return thumb.querySelector('.nzs-h2a__thumb-image') || thumb;
    });
    this.hoverZones.forEach(function (zone) {
      zone.addEventListener('mouseenter', self.onThumbEnter);
      zone.addEventListener('mouseleave', self.onThumbLeave);
    });

    this.onFocusIn = function (event) {
      if (event.target && event.target.closest && event.target.closest('[data-h2a-pause],[data-h2a-thumb]')) return;
      self.focusPaused = true;
      self.stop();
    };
    this.onFocusOut = function (event) {
      if (event.relatedTarget && self.el.contains(event.relatedTarget)) return;
      self.focusPaused = false;
      self.start();
    };
    this.el.addEventListener('focusin', this.onFocusIn);
    this.el.addEventListener('focusout', this.onFocusOut);

    this.onPause = function () {
      self.focusPaused = false;
      self.userPaused = !self.userPaused;
      self.syncPause();
      self.start();
    };
    if (this.pause) this.pause.addEventListener('click', this.onPause);

    this.onVisibility = function () {
      if (document.hidden) self.stop();
      else self.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    /* before syncPause()/start(): start() arms the countdown ring, so this.ring must exist by then */
    this.initDss();

    this.syncPause();
    this.start();

    /* NZS-H2A-MSWIPE: the phone presentation, when the section rendered one. It
       owns its own subtree and parks this rotation while it is the visible hero. */
    this.mobile = el.querySelector('[data-h2a-m]') ? new HeroM(el, this) : null;
  }

  Hero2a.prototype.resetBars = function () {
    this.bars.forEach(function (bar) {
      bar.style.transition = 'none';
      bar.style.width = '0%';
    });
  };

  Hero2a.prototype.stop = function () {
    clearTimeout(this.timer);
    this.timer = null;
    this.resetBars();
    this.ringClear();
  };

  /* hover: bank the remaining time and freeze the bar where it is */
  Hero2a.prototype.suspend = function () {
    if (!this.timer) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.remaining = Math.max(120, this.remaining - (Date.now() - this.cycleStart));
    var bar = this.bars[this.current];
    if (bar) {
      var width = getComputedStyle(bar).width;
      bar.style.transition = 'none';
      bar.style.width = width;
    }
    /* the ring banks its own progress: it was armed for the full cycle, so pausing it here and
       playing it again in resume() leaves exactly `remaining` of sweep left -- same as the timer */
    if (this.ringAnim) this.ringAnim.pause();
  };

  /* hover ends: finish the cycle from where the bar froze */
  Hero2a.prototype.resume = function () {
    var self = this;
    if (this.timer || this.count < 2 || this.userPaused || this.hoverPaused || this.focusPaused || this.editorPaused || this.mqPaused || document.hidden) return;
    this.cycleStart = Date.now();
    this.timer = setTimeout(function () {
      self.go((self.current + 1) % self.count, true);
    }, this.remaining);
    var bar = this.bars[this.current];
    if (bar) {
      void bar.offsetWidth;
      bar.style.transition = 'width ' + this.remaining + 'ms linear';
      bar.style.width = '100%';
    }
    if (this.ringAnim) this.ringAnim.play();
  };

  Hero2a.prototype.promotePhoto = function (index) {
    var photo = this.photos[index];
    var img = photo && photo.querySelector('img[data-src]');
    if (!img) return null;
    var finish;
    var ready = new Promise(function (resolve) {
      var settled = false;
      finish = function () {
        if (settled) return;
        settled = true;
        img.removeEventListener('load', finish);
        img.removeEventListener('error', finish);
        resolve();
      };
      img.addEventListener('load', finish);
      img.addEventListener('error', finish);
    });
    var srcset = img.getAttribute('data-srcset');
    if (srcset) img.setAttribute('srcset', srcset);
    img.setAttribute('fetchpriority', 'low');
    img.setAttribute('src', img.getAttribute('data-src'));
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    if (img.decode) {
      try {
        var decoding = img.decode();
        if (decoding && decoding.then) decoding.then(finish).catch(function () {});
      } catch (e) {}
    }
    if (img.complete) finish();
    return ready;
  };

  Hero2a.prototype.preloadNext = function () {
    if (this.count > 1) this.promotePhoto((this.current + 1) % this.count);
  };

  Hero2a.prototype.start = function () {
    var self = this;
    this.stop();
    this.remaining = this.delay;
    if (this.count < 2 || this.userPaused || this.hoverPaused || this.focusPaused || this.editorPaused || this.mqPaused || document.hidden) return;
    this.preloadNext();
    this.cycleStart = Date.now();
    this.timer = setTimeout(function () {
      self.go((self.current + 1) % self.count, true);
    }, this.delay);
    /* timer bar on the active thumb: reflow after resetBars() (in stop() above),
       then one linear width transition across the full rotation interval */
    var bar = this.bars[this.current];
    if (bar) {
      void bar.offsetWidth;
      bar.style.transition = 'width ' + this.delay + 'ms linear';
      bar.style.width = '100%';
    }
    this.ringArm(this.delay);
  };

  /* NZS-H2A-DSS: countdown ring around the follow-cursor. Impact drives its ring straight off the
     autoplay player -- duration is the very cycle it counts down -- so this arms from the same clock
     the rotation setTimeout uses and the two cannot drift. pathLength="100" on the circle normalises
     the geometry, so dashoffset runs 100 -> 0 regardless of the radius. */
  Hero2a.prototype.ringArm = function (ms) {
    this.ringClear();
    if (!this.ring || !this.ring.animate || reduced()) return;
    this.ringAnim = this.ring.animate(
      [{ strokeDashoffset: 100 }, { strokeDashoffset: 0 }],
      { duration: ms, easing: 'linear', fill: 'forwards' }
    );
  };

  Hero2a.prototype.ringClear = function () {
    if (!this.ringAnim) return;
    /* cancel, never merely drop the reference: fill:forwards animations accumulate on the element,
       and after a few cycles a stack of finished ones stops the ring re-arming from 100. */
    try { this.ringAnim.cancel(); } catch (e) {}
    this.ringAnim = null;
  };

  /* NZS-H2A-DSS: wires the full-bleed fade slideshow's stage -- follow-cursor, chevron direction and
     click-half prev/next. Every lookup is guarded, so on the static/rotating markup (which has no
     [data-h2a-stage]) this is a no-op and the file stays safe to deploy first, on its own.
     Cursor motion is Impact's: a direct style.translate write per pointermove, with the CSS
     transition doing the smoothing. No lerp and no rAF loop -- nothing to leak on destroy. */
  Hero2a.prototype.initDss = function () {
    var self = this;
    this.ring = this.el.querySelector('[data-h2a-ring]');
    this.ringAnim = null;
    this.stage = this.el.querySelector('[data-h2a-stage]');
    this.cursor = this.el.querySelector('[data-h2a-cursor]');
    this.dssHandlers = null;
    if (!this.stage || !this.cursor || !FINE) return;

    /* one midpoint for BOTH the chevron flip and the click, measured off the stage itself. Impact
       flips on the element's centre but advances on window.innerWidth/2 -- on anything that is not
       edge-to-edge those disagree and the arrow points the wrong way. */
    var isPrevHalf = function (clientX) {
      var rect = self.stage.getBoundingClientRect();
      return clientX < rect.left + rect.width / 2;
    };

    var overControl = function (event) {
      var t = event.target;
      return !!(t && t.closest && t.closest('a, button'));
    };

    var onMove = function (event) {
      var rect = self.stage.getBoundingClientRect();
      var x = event.clientX - rect.left - self.cursor.offsetWidth / 2;
      var y = event.clientY - rect.top - self.cursor.offsetHeight / 2;
      self.cursor.style.translate = x.toFixed(2) + 'px ' + y.toFixed(2) + 'px';
      var prev = isPrevHalf(event.clientX);
      self.cursor.classList.toggle('is-prev', prev);
      self.cursor.classList.toggle('is-next', !prev);
      self.cursor.classList.toggle('is-muted', overControl(event));
      self.cursor.classList.add('is-on');
    };

    var onLeave = function () {
      self.cursor.classList.remove('is-on', 'is-muted', 'is-prev', 'is-next');
    };

    var onClick = function (event) {
      /* a CTA click navigates and must not also advance the slide */
      if (overControl(event) || self.count < 2) return;
      self.focusPaused = false;
      self.go(isPrevHalf(event.clientX)
        ? (self.current - 1 + self.count) % self.count
        : (self.current + 1) % self.count, true);
    };

    this.stage.addEventListener('pointermove', onMove, { passive: true });
    this.stage.addEventListener('pointerleave', onLeave);
    this.stage.addEventListener('click', onClick);
    this.dssHandlers = { move: onMove, leave: onLeave, click: onClick };
  };

  /* Paused iff the pointer is over the ACTIVE card. Idempotent: suspend() banks
     the remaining time and resume() finishes the cycle from where the bar froze,
     so calling this when nothing changed must be a no-op -- hence the early
     return on an unchanged state. */
  Hero2a.prototype.syncHoverPause = function () {
    var want = this.hoverIndex > -1 && this.hoverIndex === this.current;
    if (want === this.hoverPaused) return;
    this.hoverPaused = want;
    if (want) this.suspend(); else this.resume();
  };

  Hero2a.prototype.syncPause = function () {
    if (!this.pause) return;
    this.pause.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
    this.pause.classList.toggle('is-paused', this.userPaused);
  };

  Hero2a.prototype.clearMotion = function () {
    this.texts.forEach(function (el) { el.classList.remove('is-outgoing', 'is-entering'); });
    this.photos.forEach(function (el) { el.classList.remove('is-outgoing', 'is-entering'); });
    this.stackCopies.forEach(function (el) { el.classList.remove('is-outgoing', 'is-entering'); });
    this.stackSaves.forEach(function (el) { el.classList.remove('is-outgoing', 'is-entering'); });
  };

  Hero2a.prototype.go = function (next, animate) {
    var self = this;
    if (!this.count || next < 0 || next >= this.count) return;
    if (next === this.current) {
      this.start();
      return;
    }

    this.goGen = (this.goGen || 0) + 1;
    var gen = this.goGen;
    var photoReady = this.promotePhoto(next);
    clearTimeout(this.outTimer);
    clearTimeout(this.photoTimer);
    clearTimeout(this.photoReadyTimer);

    var previous = this.current;
    var oldText = this.texts[previous];
    var oldPhoto = this.photos[previous];
    var oldCopy = this.stackCopies[previous];
    var oldSave = this.stackSaves[previous];
    var newText = this.texts[next];
    var newPhoto = this.photos[next];
    var newCopy = this.stackCopies[next];
    var newSave = this.stackSaves[next];
    var armPhotoTimer = function () {
      clearTimeout(self.photoTimer);
      self.photoTimer = setTimeout(function () {
        if (oldPhoto) oldPhoto.classList.remove('is-outgoing');
      }, 800);
    };

    this.clearMotion();

    if (oldText) {
      oldText.classList.remove('is-active');
      oldText.classList.add('is-outgoing');
      setReachable(oldText, false);
    }
    if (oldPhoto) {
      oldPhoto.classList.remove('is-active');
      oldPhoto.classList.add('is-outgoing');
      oldPhoto.setAttribute('aria-hidden', 'true');
    }
    if (oldCopy) {
      oldCopy.classList.remove('is-active');
      oldCopy.classList.add('is-outgoing');
      oldCopy.setAttribute('aria-hidden', 'true');
    }
    if (this.stackImages[previous]) {
      this.stackImages[previous].classList.remove('is-active');
      this.stackImages[previous].setAttribute('aria-hidden', 'true');
    }
    /* NZS-H2A-SAVESYNC (Blake 2026-07-31): the pill rolls out with the name and
       price it belongs to instead of swapping under them mid-transition. */
    if (oldSave) {
      oldSave.classList.remove('is-active');
      oldSave.classList.add('is-outgoing');
    }
    setReachable(this.stackLinks[previous], false);
    if (this.thumbWraps[previous]) this.thumbWraps[previous].classList.remove('is-active');
    if (this.thumbs[previous]) this.thumbs[previous].removeAttribute('aria-current');

    if (newText) {
      newText.classList.add('is-active');
      setReachable(newText, true);
      if (animate) restart(newText, 'is-entering');
    }
    if (newPhoto) {
      newPhoto.classList.add('is-active');
      newPhoto.removeAttribute('aria-hidden');
      if (animate && !photoReady) {
        restart(newPhoto, 'is-entering');
        armPhotoTimer();
      }
    }
    if (newCopy) {
      newCopy.classList.add('is-active');
      newCopy.removeAttribute('aria-hidden');
      if (animate) restart(newCopy, 'is-entering');
    }
    if (this.stackImages[next]) {
      this.stackImages[next].classList.add('is-active');
      this.stackImages[next].removeAttribute('aria-hidden');
    }
    if (newSave) {
      newSave.classList.add('is-active');
      if (animate) restart(newSave, 'is-entering');
    }
    setReachable(this.stackLinks[next], true);
    if (this.thumbWraps[next]) {
      var wrap = this.thumbWraps[next];
      wrap.classList.add('is-active');
      if (this.rail && this.rail.scrollTo) {
        this.rail.scrollTo({
          left: wrap.offsetLeft - (this.rail.clientWidth - wrap.offsetWidth) / 2,
          behavior: reduced() ? 'auto' : 'smooth'
        });
      }
    }
    if (this.thumbs[next]) this.thumbs[next].setAttribute('aria-current', 'true');

    this.current = next;
    this.syncHoverPause();   /* NZS-H2A-ACTIVEHOVER: rotation may have landed on the hovered card */
    if (animate && photoReady && newPhoto) {
      var entered = false;
      var enterPhoto = function () {
        if (self.goGen !== gen || entered || self.current !== next || !newPhoto.classList.contains('is-active')) return;
        entered = true;
        clearTimeout(self.photoReadyTimer);
        self.photoReadyTimer = null;
        restart(newPhoto, 'is-entering');
        armPhotoTimer();
      };
      var readyPhoto = function () {
        if (self.goGen !== gen) return;
        enterPhoto();
      };
      this.photoReadyTimer = setTimeout(readyPhoto, 350);
      photoReady.then(readyPhoto);
    } else if (!animate && oldPhoto) {
      oldPhoto.classList.remove('is-outgoing');
    }
    this.outTimer = setTimeout(function () {
      if (oldText) oldText.classList.remove('is-outgoing');
      if (oldCopy) oldCopy.classList.remove('is-outgoing');
      if (oldSave) oldSave.classList.remove('is-outgoing');
    }, 650);
    this.start();
  };

  Hero2a.prototype.selectBlock = function (blockId) {
    var index = -1;
    this.thumbWraps.forEach(function (wrap, i) {
      if (wrap.getAttribute('data-block-id') === blockId) index = i;
    });
    if (index < 0) return false;
    this.editorPaused = true;
    this.go(index, true);
    this.stop();
    return true;
  };

  Hero2a.prototype.releaseBlock = function (blockId) {
    var ownsBlock = this.thumbWraps.some(function (wrap) {
      return wrap.getAttribute('data-block-id') === blockId;
    });
    if (!ownsBlock) return false;
    this.editorPaused = false;
    this.start();
    return true;
  };

  Hero2a.prototype.destroy = function () {
    var self = this;
    this.goGen = (this.goGen || 0) + 1;
    this.stop();
    clearTimeout(this.outTimer);
    clearTimeout(this.photoTimer);
    clearTimeout(this.photoReadyTimer);
    this.thumbs.forEach(function (thumb, index) {
      thumb.removeEventListener('click', self.thumbHandlers[index]);
    });
    (this.hoverZones || []).forEach(function (zone) {
      zone.removeEventListener('mouseenter', self.onThumbEnter);
      zone.removeEventListener('mouseleave', self.onThumbLeave);
    });
    this.el.removeEventListener('focusin', this.onFocusIn);
    this.el.removeEventListener('focusout', this.onFocusOut);
    if (this.pause) this.pause.removeEventListener('click', this.onPause);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.ringClear();
    if (this.stage && this.dssHandlers) {
      this.stage.removeEventListener('pointermove', this.dssHandlers.move);
      this.stage.removeEventListener('pointerleave', this.dssHandlers.leave);
      this.stage.removeEventListener('click', this.dssHandlers.click);
      this.dssHandlers = null;
    }
    if (this.mobile) {
      this.mobile.destroy();
      this.mobile = null;
    }
    this.el.__nzsHero2a = null;
  };

  /* NZS-H2A-MSWIPE (Blake 2026-07-31): the mobile swipe hero's controller. It
     drives the .nzs-h2a__m subtree only -- a native scroll-snap track (nothing
     here ever preventDefaults, so vertical page pan stays the browser's), plus
     the headline, stack card and pager row that live outside the scroller and
     crossfade as the active slide changes. */
  function HeroM(el, hero) {
    var self = this;
    this.el = el;
    this.hero = hero || null;
    this.root = el.querySelector('[data-h2a-m]');
    this.track = this.root.querySelector('[data-h2a-m-track]');
    this.slides = qsa(this.root, '[data-h2a-m-slide]');
    this.heads = qsa(this.root, '[data-h2a-m-head]');
    this.cards = qsa(this.root, '[data-h2a-m-card]');
    this.thumbs = qsa(this.root, '[data-h2a-m-thumb]');
    this.hint = this.root.querySelector('[data-h2a-m-hint]');
    this.scroller = this.root.querySelector('[data-h2a-m-scroll]');
    this.count = this.slides.length;
    this.current = 0;
    this.pending = 0;
    this.delay = Math.max(2000, parseInt(el.getAttribute('data-rotate-ms'), 10) || 5000);
    this.autoOn = el.getAttribute('data-auto-rotate') === 'true';
    this.timer = null;
    this.idleTimer = null;
    this.settleTimer = null;
    this.progTimer = null;
    this.programmatic = false;
    this.touching = false;
    this.hintGone = false;
    this.io = null;
    this.thumbHandlers = [];
    this.mq = window.matchMedia ? window.matchMedia('not all and (min-width: 900px)') : null;

    this.thumbs.forEach(function (thumb, index) {
      var handler = function () {
        self.dismissHint();
        self.markUser();
        /* programmatic so the observer stays quiet while the smooth scroll
           crosses intermediate slides; scrollToIndex paints the target itself */
        self.scrollToIndex(index, true);
      };
      self.thumbHandlers.push(handler);
      thumb.addEventListener('click', handler);
    });

    /* the track's own scroll is the source of truth for "the user swiped": the
       hint retires for good on the first one, and any of them parks autoplay.
       Programmatic advances raise this.programmatic so they never look like one. */
    this.onScroll = function () {
      if (self.programmatic) return;
      self.dismissHint();
      self.markUser();
    };
    if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onTouchStart = function () {
      self.touching = true;
      /* a real finger beats a programmatic scroll: release the gate so the
         observer tracks the user's swipe instead of dropping its crossing */
      clearTimeout(self.progTimer);
      self.programmatic = false;
      self.stop();
    };
    this.onTouchEnd = function () {
      self.touching = false;
      self.markUser();
    };
    if (this.track) {
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    }

    this.onScrollBtn = function () {
      /* the schema renders this section inside Shopify's own .shopify-section
         wrapper, so the sibling to scroll to hangs off the wrapper, not off us */
      var host = self.el.closest ? (self.el.closest('.shopify-section') || self.el) : self.el;
      var next = host.nextElementSibling;
      if (!next || !next.scrollIntoView) return;
      next.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    };
    if (this.scroller) this.scroller.addEventListener('click', this.onScrollBtn);

    /* active slide = whichever fills most of the track. Settling on a short timer
       keeps a fast three-slide flick from repainting the card on the way past. */
    if (window.IntersectionObserver && this.track && this.count) {
      this.io = new IntersectionObserver(function (entries) {
        /* programmatic scrolls paint their destination up front in scrollToIndex;
           reacting here too would flash every slide the smooth scroll crosses */
        if (self.programmatic) return;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var index = self.slides.indexOf(entry.target);
          if (index < 0 || index === self.pending) return;
          self.pending = index;
          clearTimeout(self.settleTimer);
          self.settleTimer = setTimeout(function () { self.set(self.pending); }, 90);
        });
      }, { root: this.track, threshold: 0.6 });
      this.slides.forEach(function (slide) { self.io.observe(slide); });
    }

    this.onVisibility = function () {
      if (document.hidden) self.stop();
      else self.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onMq = function () { self.syncMq(); };
    if (this.mq) {
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
      else if (this.mq.addListener) this.mq.addListener(this.onMq);
    }

    this.syncMq();
  }

  HeroM.prototype.active = function () {
    return this.mq ? this.mq.matches : true;
  };

  /* exactly one of the two heroes runs at a time */
  HeroM.prototype.syncMq = function () {
    var on = this.active();
    if (this.hero) {
      this.hero.mqPaused = on;
      this.hero.start();
    }
    if (on) this.start();
    else this.stop();
  };

  HeroM.prototype.stop = function () {
    clearTimeout(this.timer);
    this.timer = null;
  };

  HeroM.prototype.start = function () {
    var self = this;
    this.stop();
    if (!this.autoOn || this.count < 2 || this.touching || document.hidden || reduced() || !this.active()) return;
    this.timer = setTimeout(function () { self.advance(); }, this.delay);
  };

  /* a real swipe wins: autoplay parks for two intervals of quiet, then resumes */
  HeroM.prototype.markUser = function () {
    var self = this;
    this.stop();
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(function () { self.start(); }, this.delay * 2);
  };

  HeroM.prototype.dismissHint = function () {
    if (this.hintGone || !this.hint) return;
    this.hintGone = true;
    this.hint.classList.add('is-gone');
  };

  HeroM.prototype.advance = function () {
    if (this.count < 2) return;
    this.scrollToIndex((this.current + 1) % this.count, true);
    this.start();
  };

  HeroM.prototype.scrollToIndex = function (index, programmatic) {
    var self = this;
    var slide = this.slides[index];
    if (!slide || !this.track || !this.track.scrollTo) return;
    if (programmatic) {
      this.programmatic = true;
      /* paint the destination immediately: the card/headline/thumb change once,
         never for the slides crossed in between. Also keeps autoplay advancing
         when IntersectionObserver is unavailable. */
      this.set(index);
      clearTimeout(this.progTimer);
      /* the 3->1 wrap crosses two slide widths of smooth scroll; 700ms could
         release mid-flight, so hold longer and let scrollend end it early */
      this.progTimer = setTimeout(function () { self.programmatic = false; }, 1100);
      if (!this.onScrollEnd) {
        this.onScrollEnd = function () {
          if (!self.programmatic) return;
          clearTimeout(self.progTimer);
          self.programmatic = false;
        };
        if ('onscrollend' in window) this.track.addEventListener('scrollend', this.onScrollEnd);
      }
    }
    this.track.scrollTo({
      left: slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2,
      behavior: reduced() ? 'auto' : 'smooth'
    });
  };

  HeroM.prototype.set = function (index) {
    if (index < 0 || index >= this.count || index === this.current) return;
    var previous = this.current;
    if (this.heads[previous]) {
      this.heads[previous].classList.remove('is-active');
      setReachable(this.heads[previous], false);
    }
    if (this.cards[previous]) {
      this.cards[previous].classList.remove('is-active');
      setReachable(this.cards[previous], false);
    }
    if (this.thumbs[previous]) {
      this.thumbs[previous].classList.remove('is-active');
      this.thumbs[previous].removeAttribute('aria-current');
    }
    if (this.heads[index]) {
      this.heads[index].classList.add('is-active');
      setReachable(this.heads[index], true);
    }
    if (this.cards[index]) {
      this.cards[index].classList.add('is-active');
      setReachable(this.cards[index], true);
    }
    if (this.thumbs[index]) {
      this.thumbs[index].classList.add('is-active');
      this.thumbs[index].setAttribute('aria-current', 'true');
    }
    this.current = index;
    this.pending = index;
  };

  HeroM.prototype.destroy = function () {
    var self = this;
    this.stop();
    clearTimeout(this.idleTimer);
    clearTimeout(this.settleTimer);
    clearTimeout(this.progTimer);
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    this.thumbs.forEach(function (thumb, index) {
      thumb.removeEventListener('click', self.thumbHandlers[index]);
    });
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('touchstart', this.onTouchStart);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('touchcancel', this.onTouchEnd);
      if (this.onScrollEnd) this.track.removeEventListener('scrollend', this.onScrollEnd);
    }
    if (this.scroller) this.scroller.removeEventListener('click', this.onScrollBtn);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.mq) {
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
      else if (this.mq.removeListener) this.mq.removeListener(this.onMq);
    }
    if (this.hero) this.hero.mqPaused = false;
  };

  function init(root) {
    qsa(root || document, '[data-nzs-hero-2a]').forEach(function (el) {
      if (el.__nzsHero2a) return;
      el.__nzsHero2a = new Hero2a(el);
      instances.push(el.__nzsHero2a);
    });
  }

  function unload(root) {
    instances = instances.filter(function (instance) {
      if (root === instance.el || root.contains(instance.el)) {
        instance.destroy();
        return false;
      }
      return true;
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unload(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var wrap = event.target && event.target.closest && event.target.closest('[data-h2a-thumb-wrap]');
    var blockId = wrap ? wrap.getAttribute('data-block-id') : (event.detail && event.detail.blockId);
    if (!blockId) return;
    instances.some(function (instance) { return instance.selectBlock(blockId); });
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var wrap = event.target && event.target.closest && event.target.closest('[data-h2a-thumb-wrap]');
    var blockId = wrap ? wrap.getAttribute('data-block-id') : (event.detail && event.detail.blockId);
    if (!blockId) return;
    instances.some(function (instance) { return instance.releaseBlock(blockId); });
  });
})();
